/** @file usePanelKeys: registers a panel's binding table and pushes its key context while it has focus. */
import { pushContext } from '@zilo/pose3d-shortcuts/pushContext';
import { registerBindings } from '@zilo/pose3d-shortcuts/registerBindings';
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import { useCallback, useEffect, useRef } from 'react';
import type { FocusEvent } from 'react';

/** Props for the panel's root element: focusable, pushes the context on focus, pops it when focus leaves. */
export type PanelKeyProps = { tabIndex: number; onFocus: () => void; onBlur: (e: FocusEvent<HTMLElement>) => void };

/** `handlers` must be stable (memoised) and read state lazily: `registerBindings` copies them once per effect run. */
export function usePanelKeys(context: string, table: Binding[], handlers: Record<string, () => void>): PanelKeyProps {
  useEffect(() => registerBindings(context, table, handlers), [context, table, handlers]);
  const pop = useRef<(() => void) | null>(null);
  useEffect(() => () => { pop.current?.(); pop.current = null; }, []);
  const onFocus = useCallback(() => { pop.current ??= pushContext(context); }, [context]);
  const onBlur = useCallback((e: FocusEvent<HTMLElement>) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    pop.current?.();
    pop.current = null;
  }, []);
  return { tabIndex: 0, onFocus, onBlur };
}
